import React, { useMemo, useState } from "react";
import "./dashboard-cards.css";

const FWB_METRICS = [
  {
    id: "gait",
    title: "Gait Speed",
    icon: "🚶",
    field: "gaitSpeed",
    question:
      "Based on your observations over the past week, how would you describe your loved one's walking?",
    options: [
      { label: "Normal Walking", value: 5 },
      { label: "Slow Walking", value: 4 },
      { label: "Shuffle", value: 3 },
      { label: "Limp", value: 2 },
      { label: "Wobbly", value: 1 },
      { label: "Unable to walk", value: 0 },
    ],
    getValue: (data) =>
      data.gaitSpeed !== undefined && data.gaitSpeed !== null && data.gaitSpeed !== ""
        ? String(data.gaitSpeed)
        : "",
    scoreFrom: (data) => {
      const n = Number(data.gaitSpeed);
      return Number.isFinite(n) ? Math.round((n / 5) * 100) : 0;
    },
  },
  {
    id: "mobility",
    title: "Mobility",
    icon: "🦽",
    field: "mobilityDevice",
    question:
      "When moving around inside the home, does your loved one typically use a mobility aid, or do they walk without one?",
    options: [
      { label: "Walks Freely", value: 4 },
      { label: "Uses a Cane", value: 3 },
      { label: "Uses a Walker", value: 2 },
      { label: "Uses a Wheelchair", value: 1 },
    ],
    getValue: (data) =>
      data.mobilityDevice !== undefined &&
      data.mobilityDevice !== null &&
      data.mobilityDevice !== ""
        ? String(data.mobilityDevice)
        : "",
    scoreFrom: (data) => {
      const n = Number(data.mobilityDevice);
      return Number.isFinite(n) ? Math.round((n / 4) * 100) : 0;
    },
  },
  {
    id: "grip",
    title: "Grip Strength",
    icon: "✊",
    field: "gripStrength",
    question:
      "How would you describe your loved one's ability to grip and hold everyday objects (such as a cup, utensils, or a doorknob)?",
    options: [
      { label: "Strong", value: 4 },
      { label: "Moderate", value: 3 },
      { label: "Weak", value: 2 },
      { label: "Very Weak", value: 1 },
    ],
    getValue: (data) => {
      const right = Number(data.gripRight);
      const left = Number(data.gripLeft);
      const value = Number.isFinite(right) ? right : Number.isFinite(left) ? left : "";
      return value !== "" ? String(value) : "";
    },
    scoreFrom: (data) => {
      const right = Number(data.gripRight);
      const left = Number(data.gripLeft);
      const grip = Math.max(
        Number.isFinite(right) ? right : 0,
        Number.isFinite(left) ? left : 0,
      );
      return grip > 0 ? Math.round((grip / 4) * 100) : 0;
    },
  },
  {
    id: "falls",
    title: "Falls History",
    icon: "⚠️",
    field: "fallsLastYear",
    question: "How many falls has your loved one experienced in the past 12 months?",
    options: [
      { label: "0 falls", value: 3 },
      { label: "1 fall", value: 2 },
      { label: "≥2 falls (high fall risk)", value: 1 },
    ],
    getValue: (data) =>
      data.fallsLastYear !== undefined &&
      data.fallsLastYear !== null &&
      data.fallsLastYear !== ""
        ? String(data.fallsLastYear)
        : "",
    scoreFrom: (data) => {
      const n = Number(data.fallsLastYear);
      return Number.isFinite(n) ? Math.round((n / 3) * 100) : 0;
    },
  },
];

const FwbMetricCard = ({
  metric,
  data,
  expanded,
  onToggle,
  onChange,
  disabled,
}) => {
  const score = metric.scoreFrom(data);
  const selectedValue = metric.getValue(data);
  const selectedLabel =
    metric.options.find((opt) => String(opt.value) === selectedValue)?.label || "";

  const handleSelectChange = (event) => {
    const value = Number(event.target.value);
    if (!Number.isFinite(value)) return;
    if (metric.field === "gripStrength") {
      onChange?.("gripRight", value);
      onChange?.("gripLeft", value);
      return;
    }
    onChange?.(metric.field, value);
  };

  return (
    <div className={`breakdown-card breakdown-card-interactive ${expanded ? "expanded" : ""}`}>
      <button
        type="button"
        className="breakdown-card-toggle"
        onClick={onToggle}
        aria-expanded={expanded}
        aria-controls={`fwb-metric-${metric.id}`}
      >
        <div className="breakdown-card-header">
          <div className="breakdown-card-icon">{metric.icon}</div>
          <div className="breakdown-card-heading">
            <div className="breakdown-card-title">{metric.title}</div>
            {selectedLabel && !expanded ? (
              <div className="breakdown-card-selection">{selectedLabel}</div>
            ) : null}
          </div>
        </div>
        <div className="breakdown-card-meta">
          <div className="breakdown-card-score">{score}</div>
          <span className="breakdown-card-chevron" aria-hidden="true">
            {expanded ? "▾" : "▸"}
          </span>
        </div>
      </button>

      {expanded ? (
        <div className="breakdown-card-body" id={`fwb-metric-${metric.id}`}>
          <p className="breakdown-card-question">{metric.question}</p>
          <select
            id={`fwb-select-${metric.id}`}
            className="breakdown-card-select"
            value={selectedValue}
            onChange={handleSelectChange}
            disabled={disabled}
            aria-label={metric.question}
          >
            <option value="">Select an option</option>
            {metric.options.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      ) : null}
    </div>
  );
};

const WellBeingDetailsSection = ({ data = {}, onChange, disabled = false }) => {
  const [expandedId, setExpandedId] = useState(null);

  const metrics = useMemo(
    () =>
      FWB_METRICS.map((metric) => ({
        ...metric,
        score: metric.scoreFrom(data),
      })),
    [data],
  );

  return (
    <div className="section-container">
      <h2 className="section-title">Well-Being Score Breakdown</h2>

      <div className="breakdown-grid">
        {metrics.map((metric) => (
          <FwbMetricCard
            key={metric.id}
            metric={metric}
            data={data}
            expanded={expandedId === metric.id}
            onToggle={() =>
              setExpandedId((prev) => (prev === metric.id ? null : metric.id))
            }
            onChange={onChange}
            disabled={disabled}
          />
        ))}
      </div>
    </div>
  );
};

export default WellBeingDetailsSection;
